import { useEffect, useCallback, useState } from "react";
import { api, API_BASE, authedFetch } from "@/lib/api";
import { ArrowRight, ShieldAlert, Building2, Download, Loader2, Lock } from "lucide-react";
import { cn, humanCategory, letterColor, scoreTone, riskTone } from "@/lib/util";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { usePermissions } from "@/lib/permissions";
import { RoiDashboard } from "@/components/security/RoiDashboard";

export default function Reports() {
  const { t, locale } = useT();
  const { can } = usePermissions();
  const [rating, setRating] = useState(null);
  const [findings, setFindings] = useState([]);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("exec");
  const [selClient, setSelClient] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadingPortfolio, setDownloadingPortfolio] = useState(false);

  const load = useCallback(async () => {
    const [r, f, c] = await Promise.all([
      api.get("/ratings/current").catch(() => ({ data: null })),
      api.get("/findings?limit=20"),
      api.get("/clients"),
    ]);
    setRating(r.data); setFindings(f.data); setClients(c.data);
    if (c.data.length) setSelClient(c.data[0].id);
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const url = selClient
        ? `${API_BASE}/reports/executive.pdf?client_id=${encodeURIComponent(selClient)}`
        : `${API_BASE}/reports/executive.pdf`;
      const resp = await authedFetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const client = clients.find(c => c.id === selClient);
      const safeName = (client?.name || "report").replace(/[^A-Za-z0-9_-]+/g, "_");
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `CIBRAXIS-Executive-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success(t("reports.download_toast"));
    } catch (e) {
      toast.error(t("reports.download_failed", { msg: e.message }));
    } finally { setDownloading(false); }
  };

  const downloadPortfolioPdf = async () => {
    setDownloadingPortfolio(true);
    try {
      const resp = await authedFetch(`${API_BASE}/reports/portfolio.pdf`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      const dlUrl = URL.createObjectURL(blob);
      a.href = dlUrl;
      a.download = `CIBRAXIS-Portfolio-ENS.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success(t("reports.portfolio_downloaded"));
    } catch (e) {
      toast.error(t("reports.download_failed", { msg: e.message }));
    } finally { setDownloadingPortfolio(false); }
  };

  const dateLocale = locale === "en" ? "en-US" : "es-ES";
  const tabs = [
    { id: "exec", label: t("reports.tab.exec") },
    { id: "tech", label: t("reports.tab.tech") },
    { id: "portfolio", label: t("reports.tab.portfolio") },
    { id: "roi", label: "ROI y eficiencia" },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">{t("reports.overline")}</div>
          <h1 className="font-display font-bold tracking-tighter" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>{t("reports.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("reports.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selClient}
            onChange={(e) => setSelClient(e.target.value)}
            className="bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
            data-testid="pdf-client-select"
          >
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {can("report.download") ? (
            <>
              <button
                onClick={downloadPdf}
                disabled={downloading || !selClient}
                className="border border-primary bg-primary/10 text-primary px-4 py-2 text-sm hover:bg-primary/20 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="download-pdf-btn"
              >
                {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {t("reports.download")}
              </button>
              <button
                onClick={downloadPortfolioPdf}
                disabled={downloadingPortfolio || clients.length === 0}
                className="border border-sev-medium/70 bg-sev-medium/10 text-sev-medium px-4 py-2 text-sm hover:bg-sev-medium/20 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="download-portfolio-pdf-btn"
                title={t("reports.download_portfolio_hint")}
              >
                {downloadingPortfolio ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {t("reports.download_portfolio")}
              </button>
            </>
          ) : (
            <div className="border border-border px-3 py-2 text-xs font-mono text-muted-foreground inline-flex items-center gap-2"
                 data-testid="download-locked-badge">
              <Lock className="w-3 h-3" /> Descarga no permitida para tu rol
            </div>
          )}
        </div>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        {tabs.map(tt => (
          <button key={tt.id} onClick={() => setTab(tt.id)}
                  className={cn("font-mono text-[10px] tracking-widest uppercase px-3 py-2 border",
                    tab === tt.id ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground")}
                  data-testid={`report-tab-${tt.id}`}>
            {tt.label}
          </button>
        ))}
      </div>

      {tab === "exec" && rating && <ExecutivePreview rating={rating} t={t} dateLocale={dateLocale} />}
      {tab === "tech" && <TechnicalPreview rating={rating} findings={findings} t={t} dateLocale={dateLocale} />}
      {tab === "portfolio" && <PortfolioPreview clients={clients} t={t} />}
      {tab === "roi" && <RoiDashboard clients={clients} />}
    </div>
  );
}

function ExecutivePreview({ rating, t, dateLocale }) {
  return (
    <div className="border border-border bg-card/60 p-8 max-w-4xl" data-testid="report-exec-preview">
      <div className="overline">{t("reports.exec.overline")}</div>
      <h2 className="font-display text-3xl font-medium mt-2">{t("reports.exec.title")}</h2>
      <div className="mt-1 font-mono text-[10px] text-muted-foreground tracking-wider uppercase">
        {t("reports.exec.generated", { date: new Date().toLocaleDateString(dateLocale), ver: rating.algorithm_version })}
      </div>

      <div className="mt-8 flex items-baseline gap-6 border-y border-border py-6">
        <span className={cn("font-display font-bold leading-none", letterColor(rating.overall_letter))} style={{ fontSize: 96 }}>
          {rating.overall_letter}
        </span>
        <div>
          <div className="font-display text-4xl">{rating.overall_score}<span className="text-muted-foreground text-lg">/100</span></div>
          <div className="font-mono text-xs text-muted-foreground mt-1">
            {t("reports.exec.overall", { trend: rating.trend, delta: (rating.delta_vs_prev >= 0 ? "+" : "") + rating.delta_vs_prev })}
          </div>
        </div>
      </div>

      <section className="mt-6">
        <div className="overline mb-2">{t("reports.exec.drivers")}</div>
        <ul className="text-sm list-none space-y-2">
          {rating.top_reducers.slice(0, 5).map((r) => (
            <li key={r} className="border-l-2 border-sev-critical pl-3 py-1 font-mono text-xs">{r}</li>
          ))}
        </ul>
      </section>

      <section className="mt-6">
        <div className="overline mb-2">{t("reports.exec.actions")}</div>
        <ul className="text-sm list-none space-y-2">
          {rating.top_improvers.slice(0, 5).map((r) => (
            <li key={r} className="border-l-2 border-sev-low pl-3 py-1 font-mono text-xs">{r}</li>
          ))}
        </ul>
      </section>

      <section className="mt-6">
        <div className="overline mb-2">{t("reports.exec.breakdown")}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {rating.categories.map(c => (
            <div key={c.category} className="flex items-center justify-between border-b border-border/50 py-2 text-sm">
              <span>{humanCategory(c.category)}</span>
              <span className={cn("font-mono font-bold", scoreTone(c.score).text)}>{c.score}/100 · {c.letter}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 pt-4 border-t border-border font-mono text-[10px] tracking-wider uppercase text-muted-foreground">
        {t("reports.exec.footer")}
      </div>
    </div>
  );
}

function TechnicalPreview({ rating, findings, t, dateLocale }) {
  return (
    <div className="border border-border bg-card/60 p-8 max-w-5xl" data-testid="report-tech-preview">
      <div className="overline">{t("reports.tech.overline")}</div>
      <h2 className="font-display text-3xl font-medium mt-2">{t("reports.tech.title")}</h2>
      <div className="mt-1 font-mono text-[10px] text-muted-foreground tracking-wider uppercase">
        {t("reports.tech.generated", { date: new Date().toLocaleDateString(dateLocale), count: findings.length })}
      </div>

      {rating && (
        <div className="mt-6 grid grid-cols-3 gap-4 font-mono text-xs">
          <div><span className="overline block">{t("reports.tech.overall")}</span><span className="text-2xl font-display mt-1 block">{rating.overall_score}</span></div>
          <div><span className="overline block">{t("reports.tech.letter")}</span><span className={cn("text-2xl font-display mt-1 block", letterColor(rating.overall_letter))}>{rating.overall_letter}</span></div>
          <div><span className="overline block">{t("reports.tech.trend")}</span><span className="text-2xl font-display mt-1 block">{rating.trend}</span></div>
        </div>
      )}

      <div className="mt-6">
        <div className="overline mb-2">{t("reports.tech.findings")}</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left overline border-b border-border">
              <th className="p-2">{t("reports.tech.col.title")}</th>
              <th className="p-2">{t("findings.col.sev")}</th>
              <th className="p-2">{t("findings.col.risk")}</th>
              <th className="p-2">{t("findings.col.cvss")}</th>
              <th className="p-2">{t("findings.col.epss")}</th>
              <th className="p-2">{t("findings.col.category")}</th>
              <th className="p-2">{t("findings.col.asset")}</th>
            </tr>
          </thead>
          <tbody>
            {findings.map(f => (
              <tr key={f.id} className="border-b border-border/50">
                <td className="p-2">
                  <Link to={`/findings/${f.id}`} className="hover:text-primary">
                    <div>{f.title}</div>
                    {f.cve && <div className="font-mono text-[10px] text-muted-foreground">{f.cve}{f.kev && <span className="ml-2 text-sev-critical">· KEV</span>}</div>}
                  </Link>
                </td>
                <td className="p-2 font-mono text-[10px] tracking-widest uppercase">{f.severity}</td>
                <td className="p-2 font-mono">{f.risk_score}</td>
                <td className="p-2 font-mono">{f.cvss ?? "—"}</td>
                <td className="p-2 font-mono">{f.epss ? `${(f.epss * 100).toFixed(0)}%` : "—"}</td>
                <td className="p-2 text-xs">{humanCategory(f.category)}</td>
                <td className="p-2 font-mono text-[11px] text-muted-foreground truncate max-w-[180px]">{f.asset_label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 pt-4 border-t border-border font-mono text-[10px] tracking-wider uppercase text-muted-foreground">
        {t("reports.tech.footer")}
      </div>
    </div>
  );
}

function PortfolioPreview({ clients, t }) {
  return (
    <div className="border border-border bg-card/60 p-8 max-w-5xl" data-testid="report-portfolio-preview">
      <div className="overline">{t("reports.portfolio.overline")}</div>
      <h2 className="font-display text-3xl font-medium mt-2">{t("reports.portfolio.title")}</h2>
      <div className="mt-1 font-mono text-[10px] text-muted-foreground tracking-wider uppercase">
        {t("reports.portfolio.subtitle", { count: clients.length })}
      </div>

      <div className="mt-6 space-y-2">
        {clients.map(c => (
          <Link key={c.id} to={`/clients/${c.id}`} className="flex items-center gap-4 border border-border p-3 hover:bg-muted/30 transition-colors">
            <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{c.name}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{c.industry || "—"} · {c.assets_count} · {c.total}</div>
            </div>
            <div className="text-right">
              <div className={cn("font-display font-bold text-2xl", riskTone(c.risk_score).text)}>{c.risk_score}</div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">/100</div>
            </div>
            {c.critical > 0 && (
              <div className="hidden md:flex items-center gap-1 text-sev-critical font-mono text-xs">
                <ShieldAlert className="w-3 h-3" />{c.critical}
              </div>
            )}
            <ArrowRight className="w-3 h-3 text-muted-foreground" />
          </Link>
        ))}
      </div>

      <div className="mt-8 pt-4 border-t border-border font-mono text-[10px] tracking-wider uppercase text-muted-foreground">
        {t("reports.portfolio.footer")}
      </div>
    </div>
  );
}
